import { Button, Form, Row, Space, Table } from "antd";
import { AdvancedForm } from "../form/advanced-form";
import {
  useGetCustomersQuery, 
  useGetEmployeeClosuresQuery as getEmployeeClosuresQuery, 
  useGetEmployeesQuery, 
  useGetWorkTimeEmployeeClosuresCrossReferencesQuery, 
  useGetWorkTimeQuery, useGetWorkTimesQuery, 
  useDeleteWorkTimeEmployeeClosuresCrossReferenceMutation, 
  useCreateWorkTimeEmployeeClosuresCrossReferenceMutation, 
  useGetWorkTimeSignaturesQuery, 
  useDeleteWorkTimeSignatureMutation 
} from "../../../api/plamo-api";
import FormItem, { createRequiredRule } from "../form/form-item";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import dayjs from "dayjs";
import PlamoApiWithSaveNotifications from "../util/with-save-notification";
import ConfirmButton from "../util/confirm-button";

const { 
  useCreateWorkTimeWithSaveNotificationMutation,
  useUpdateWorkTimeWithSaveNotificationMutation, 
  useDeleteWorkTimeWithSaveNotificationMutation 
} = PlamoApiWithSaveNotifications;

const ChangeRequestTable = ({ worktime, changeRequests }) => {
  const [newChangeRequest, setNewChangeRequest] = useState<any>(changeRequests[0])
  const [newWorktime, setNewWorktime] = useState<any>(worktime)
  
  const [updateWorkTime] = useUpdateWorkTimeWithSaveNotificationMutation();
  const [deleteWorkTime] = useDeleteWorkTimeWithSaveNotificationMutation();

  const columns = useMemo(() => [
    {
      dataIndex: "title",
      title: "Wert",
      render: v => <strong>{ v }</strong>
    },
    {
      dataIndex: "originalStr",
      title: "Original",
      render: (value, record) => <span style={{ color: !record.changed ? 'gray' : undefined, textDecoration: record.changed ? 'line-through' : undefined }}>{ value }</span>
    },
    {
      dataIndex: "changeStr",
      title: "Änderung",
      render: (value, record) => <span style={{ color: !record.changed ? 'gray' : undefined }}>{ value }</span>
    },
    {
      dataIndex: "option",
      title: "Option",
      render: (_, record) => record.changed && <Space>
        <Button onClick={() => setNewWorktime(o => ({...o, [record.attribute]: record.change}))}>Änderung genehmigen</Button>
        <Button onClick={() => setNewChangeRequest(o => ({...o, [record.attribute]: record.original}))}>Änderung ablehnen</Button>      
      </Space>
    },
  ], [setNewWorktime])

  const data = useMemo(() => [
    {
      title: "Beginn",
      attribute: "startTime",
      render: s => dayjs(s).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: "Ende",
      attribute: "endTime",
      render: s => dayjs(s).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: "Abw. Arbeitszeit Beginn",
      attribute: "startTimeEmployee",
      render: s => dayjs(s).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: "Abw. Arbeitszeit Ende",
      attribute: "endTimeEmployee",
      render: s => dayjs(s).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: "KM hin",
      attribute: "distanceToManual",
      render: s => (s || 0).toFixed(1),
    },
    {
      title: "KM zurück",
      attribute: "distanceFromManual",
      render: s => (s || 0).toFixed(1),
    },
    {
      title: "KM Einkauf",
      attribute: "distanceShopping",
      render: s => (s || 0).toFixed(1),
    },
    {
      title: "Tätigkeit",
      attribute: "activities",
    },
    {
      title: "Anmerkungen",
      attribute: "remarks",
    },
    /*
    {
      title: "Kunde",
      attribute: "customerId",
      original: "a",
      change: "b",
    },
    */
  ].map(i => ({
    key: i.attribute,
    attribute: i.attribute,
    title: i.title,
    original: newWorktime[i.attribute],
    change: newChangeRequest[i.attribute],
    originalStr: ((i.render || (a => a))(newWorktime[i.attribute])),
    changeStr: ((i.render || (a => a))(newChangeRequest[i.attribute])),
    changed: ((i.render || (a => a))(newWorktime[i.attribute])) !== ((i.render || (a => a))(newChangeRequest[i.attribute])),
  })), [newWorktime, newChangeRequest])

  const hasChanges = data.map(i => i.changed).reduce((p, c) => p || c, false)

  const reset = useCallback(() => {
    setNewWorktime(worktime)
    setNewChangeRequest(changeRequests[0])
  }, [worktime, changeRequests, setNewWorktime, setNewChangeRequest])

  const save = useCallback(async () => {
    await updateWorkTime(newWorktime)
    await Promise.all(changeRequests.map(cr => deleteWorkTime(cr)))
  }, [newWorktime, updateWorkTime, changeRequests, deleteWorkTime])

  return <>
    <p>Diese Zeitbuchung wurde geändert!</p>
    <Table
      columns={columns}
      dataSource={data}
      pagination={false}
    />
    <div style={{ textAlign: 'right', paddingRight: "2em" }}>
      <Space size="small">
        <Button onClick={reset}>
          Zurücksetzen
        </Button>
        <Button type="primary" disabled={hasChanges} onClick={save}>
          Speichern
        </Button>
      </Space>
    </div>
  </>
}

function WorktimeDetails() {
  const { id: worktimeIdStr } = useParams();
  const worktimeId = worktimeIdStr !== 'new' ? parseInt(worktimeIdStr || '0') : null;
  const { data: signatures } = useGetWorkTimeSignaturesQuery({ f: JSON.stringify({ "workTime.id": { c: "EQ", v: worktimeId } }), st: JSON.stringify({ id: "desc" })}, { skip: !worktimeId}) as any;
  const { data: changeRequests } = useGetWorkTimesQuery({ f: JSON.stringify({ "changeRequestFor.id": { c: "EQ", v: worktimeId } }), st: JSON.stringify({ id: "desc" })}, { skip: !worktimeId}) as any;
  const { data: closureCrossReferences } = useGetWorkTimeEmployeeClosuresCrossReferencesQuery({ f: JSON.stringify({ "workTime.id": { c: "EQ", v: worktimeId } }), st: JSON.stringify({ id: "desc" })}, { skip: !worktimeId}) as any;
  //const changeRequest = useMemo(() => (changeRequests?.items || [])[0], [changeRequests])
  const { data: worktime } = useGetWorkTimeQuery({ id: worktimeId }, { skip: !worktimeId }) as any;
  const [createWorkTime] = useCreateWorkTimeWithSaveNotificationMutation();
  const [updateWorkTime] = useUpdateWorkTimeWithSaveNotificationMutation();
  const [deleteWorkTime] = useDeleteWorkTimeWithSaveNotificationMutation();
  const [deleteWorkTimeSignature] = useDeleteWorkTimeSignatureMutation();
  const [deleteWorkTimeEmployeeClosuresCrossReference] = useDeleteWorkTimeEmployeeClosuresCrossReferenceMutation();
  const [createWorkTimeEmployeeClosuresCrossReference] = useCreateWorkTimeEmployeeClosuresCrossReferenceMutation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedEmployeeId = Form.useWatch("employeeId", form);
  const [search] = useSearchParams();
  const preselectedEmployeeId = useMemo(() => parseInt(search.get('preselectedEmployeeId') || '') || undefined, [search])
  const preselectedCustomerId = useMemo(() => parseInt(search.get('preselectedCustomerId') || '') || undefined, [search])

  const worktimeWithEmployeeClosures = useMemo(() => ({...(worktime || { customerId: preselectedCustomerId, employeeId: preselectedEmployeeId }), employeeClosureIds: closureCrossReferences?.items?.map(c => c.employeeClosureId)}), 
      [worktime, closureCrossReferences, preselectedCustomerId, preselectedEmployeeId])

  console.log("Work time with closures", worktimeWithEmployeeClosures)
  
  const onFinish = useCallback(async (values: any) => {
    let changedWorktimeId;
    if(!worktimeId) {
      const { data: newWorktime }:any = await createWorkTime(values);
      console.log("Created new worktime", newWorktime);
      changedWorktimeId = newWorktime.id;
    } else {
      const newWorktime = {...worktime, ...values}
      await updateWorkTime(newWorktime);
      changedWorktimeId = worktimeId;
    }

    const deletedEmployeeClosureCrossReferences = closureCrossReferences?.items?.filter(c => !values.employeeClosureIds || !values.employeeClosureIds.includes(c.employeeClosureId));
    //console.log("Deleted employee closure cross references", deletedEmployeeClosureCrossReferences, closureCrossReferences, values.employeeClosureIds);

    let promises = deletedEmployeeClosureCrossReferences?.map(c => deleteWorkTimeEmployeeClosuresCrossReference(c)) || [];
    await Promise.all(promises);

    const existingEmployeeClosureCrossReferencesIds = closureCrossReferences?.items?.map(c => c.employeeClosureId);
    const createdEmployeeClosureCrossReferencesIds = (values.employeeClosureIds || []).filter(id => !existingEmployeeClosureCrossReferencesIds || !existingEmployeeClosureCrossReferencesIds.includes(id))

    promises = createdEmployeeClosureCrossReferencesIds.map(c => createWorkTimeEmployeeClosuresCrossReference({ workTimeId: changedWorktimeId, employeeClosureId: c }))
    await Promise.all(promises);

    if(!worktimeId) {
      navigate(`./../${changedWorktimeId}`, { replace: true })
    }
  }, [worktimeId, createWorkTime, updateWorkTime, navigate, worktime, closureCrossReferences, deleteWorkTimeEmployeeClosuresCrossReference, createWorkTimeEmployeeClosuresCrossReference]);

  const isLoading = false;

  const copyTimes = useCallback(() => {
    const values = form.getFieldsValue();
    const newValues = {...values, startTimeEmployee: values.startTime, endTimeEmployee: values.endTime };
    form.setFieldsValue(newValues);
  }, [form])

  const onDelete = useCallback(async () => {
    const promises = signatures?.items?.map(deleteWorkTimeSignature)

    await Promise.all(promises);

    await deleteWorkTime({ id: worktimeId })
    navigate(-1)
  }, [deleteWorkTime, worktimeId, navigate, signatures, deleteWorkTimeSignature])

  const getEmployeeClosuresQueryForEmployeeId = useCallback(({f, ...rest}) => {
    const existingFilter = JSON.parse(f || '{}');
    const newFilter = { ...existingFilter, "employee.id": { c: "EQ", v: selectedEmployeeId}}
    return getEmployeeClosuresQuery({ f: JSON.stringify(newFilter), ...rest });
  }, [selectedEmployeeId]);

  return <>
    { changeRequests?.items?.length > 0 && <ChangeRequestTable worktime={worktimeWithEmployeeClosures} changeRequests={changeRequests?.items} /> }
    { (worktimeIdStr === 'new' || changeRequests?.items?.length === 0) && <AdvancedForm form={form} name="worktime" onFinish={onFinish} isLoading={isLoading} originalValues={worktimeWithEmployeeClosures} initialEditing={worktimeIdStr === 'new'}>
      <Row><h3>Personen</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="customerId" label="Kunde" readOnly={!!preselectedCustomerId} type="ref" typeData={{ query: useGetCustomersQuery, getId: customer => customer.id, render: customer => `${customer.firstName} ${customer.lastName}` }} />
        <FormItem fieldName="employeeId" label="Mitarbeiter" readOnly={!!preselectedEmployeeId} type="ref" rules={[createRequiredRule('Mitarbeiter darf nicht leer sein')]} typeData={{ query: useGetEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`}} />
        <FormItem fieldName="budget" label="Budget" type="enum" typeData={{ options: [{ value: 'supportAmount', label: "Entlastungsbetrag" }, { value: "absenceCare", label: "Verhinderungspflege" }, { value: "conversion", label: "Umwandlung" }, { value: "otherBudgetAmount", label: "Sonstiges"}] }} />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="substitute" label="Vertretung" type="check" readOnly />
        <FormItem fieldName="substitutionCustomerFirstName" label="Vertretung Vorname" readOnly />
        <FormItem fieldName="substitutionCustomerLastName" label="Vertretung Nachname" readOnly />
      </Row>
      <Row><h3>Andere Tätigkeit </h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="otherTask" label="Tätigkeit" type="backendEnum" typeData={{ ident: "otherTask" }} />
      </Row>
      <Row><h3>Abgerechnete Zeit</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="startTime" label="Beginn" type="datetime" />
        <FormItem fieldName="endTime" label="Ende" type="datetime" />
      </Row>
      <Row><h3>Arbeitszeit</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="copyFromCustomerTimes" label="Abger. Zeit kopieren" type="button" typeData={{ caption: "Kopieren", onClick: copyTimes }} />
      </Row>
      <Row gutter={24}>
        <FormItem fieldName="startTimeEmployee" label="Beginn" type="datetime" />
        <FormItem fieldName="endTimeEmployee" label="Ende" type="datetime" />
      </Row>
      <Row><h3>Strecken</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="distanceFromManual" label="Kilometer hin" type="number" />
        <FormItem fieldName="distanceToManual" label="Kilometer zurück" type="number" />
        <FormItem fieldName="arrivalFromPreviousCustomer" label="Anf. v. vorh. K." type="check" />
        <FormItem fieldName="distanceShopping" label="Kilometer Einkauf" type="number" />
      </Row>
      <Row><h3>Sonstiges</h3></Row>
      <Row gutter={24}>
        <FormItem fieldName="activities" label="Aktivität" />
        <FormItem fieldName="remarks" label="Anmerkungen" />
        <FormItem fieldName="employeeClosureIds" label="Abrechnungen" type="ref" typeData={{ query: getEmployeeClosuresQueryForEmployeeId, getId: c => c.id, render: c => `${c.id} ${c.title}`, multiple: true }} />
      </Row>
      <Row><h3>Unterschrift</h3></Row>
      <Row>
        { signatures?.items?.map(s => <img src={s.signatureImage} alt="Unterschrift" style={{ height: "7em", backgroundColor: "white" }} />)}
      </Row>
      <Row><h3>Historie</h3></Row>
      <Row>
        <FormItem fieldName="createdBy" label="Erstellt von" type="user" typeData={{ noFilter: true }} readOnly/>
        <FormItem fieldName="createdAt" label="Erstellt am" type="datetime" readOnly />
        <FormItem fieldName="updatedBy" label="Geändert von" type="user" typeData={{ noFilter: true }} readOnly/>
        <FormItem fieldName="updatedAt" label="Geändert am" type="datetime" readOnly />
      </Row>
      { worktimeId && <>
        <Row><h3>Zeitbuchung löschen</h3></Row>
        <div>
          <Space size="small">
            <ConfirmButton type="default" danger onClick={onDelete} confirmTitle={`Zeitbuchung löschen?`} confirmContent={'Wollen Sie die Zeitbuchung wirklich löschen?'}>
              Zeitbuchung löschen
            </ConfirmButton>
          </Space>
        </div>
      </>}
    </AdvancedForm>}
  </>
}

export default WorktimeDetails;